'use client';

import { type MouseEventHandler, useCallback, useContext, useMemo, useRef } from 'react';

import { useInteractiveElementKeyDownCallback } from '../../../hooks/use-interactive-element-key-down-callback';
import { TableHeaderCellContext } from '../context/table-header-cell.context';
import { TableRootContext } from '../context/table-root.context';
import { TableSortButton } from '../table-sort-button';
import type { TableHeaderCellPropsInterface } from '../types/table-header-cell.types';

/**
 * Narrowed input type — only the sort-related slice of `TableHeaderCellPropsInterface`.
 * Lets the consumer (`Table.HeaderCell`) keep its remaining `rest` to spread
 * directly onto the `<th>` element without round-tripping through the hook.
 */
type UseSortableHeaderCellPropsType = Pick<
  TableHeaderCellPropsInterface,
  'align' | 'children' | 'onClick' | 'onKeyDown' | 'scope' | 'sortDirectionOrder' | 'value'
>;

/**
 * A hook that transforms table header cells into interactive sorting controls.
 *
 * Enhances table header cells with sorting functionality when appropriate conditions
 * are met (has a value prop and is within a header). Handles both mouse and keyboard
 * interactions, manages sorting state, and provides visual feedback through a sort button.
 */
export const useSortableHeaderCell = (props: UseSortableHeaderCellPropsType) => {
  const {
    align,
    children,
    onClick,
    onKeyDown,
    scope: scopeProp,
    sortDirectionOrder: userSortDirectionOrder,
    value,
  } = props;

  // Get sorting controls and parent context
  const { onSortChange, sortBy, sortDirection, sortDirectionOrder } = useContext(TableRootContext);
  const { parent } = useContext(TableHeaderCellContext);

  // Reference to the table cell DOM element for keyboard interactions
  const ref = useRef<HTMLTableCellElement>(null);

  // Determine scope based on parent context or prop override
  const scope = useMemo(
    () => scopeProp ?? (parent === 'body' ? 'row' : 'col'),
    [scopeProp, parent],
  );

  // Cell is sortable if it has a value and is in the header
  const sortable = !!value && parent === 'header';

  // Calculate the next sort direction based on current state
  const getNextSortDirection = useCallback(() => {
    const sortOrder = userSortDirectionOrder || sortDirectionOrder!;

    // If this is a new sort column, use first direction
    if (sortBy !== value) {
      return sortOrder[0];
    }

    // Find current direction index and get next
    const currentIndex = sortOrder.indexOf(sortDirection!);
    const nextIndex = (currentIndex + 1) % sortOrder.length;

    return sortOrder[nextIndex];
  }, [sortBy, value, sortDirection, sortDirectionOrder, userSortDirectionOrder]);

  // Handle sort change event
  const onSortChangeCallback = useCallback<MouseEventHandler<HTMLTableCellElement>>(
    (event) => {
      if (!sortable) return;

      // Trigger sort change
      onSortChange?.(
        {
          sortBy: value!,
          sortDirection: getNextSortDirection(),
        },
        event,
      );

      // Preserve custom click handler if provided
      onClick?.(event);
    },
    [sortable, value, onSortChange, onClick, getNextSortDirection],
  );

  // Set up keyboard interaction handlers
  const onKeyDownCallback = useInteractiveElementKeyDownCallback({
    ref,
    onClick: sortable ? onSortChangeCallback : onClick,
    onKeyDown,
    bubbleUp: true,
  });

  // Compute cell props based on sortability
  const cellProps = useMemo(
    () => ({
      onClick: sortable ? onSortChangeCallback : onClick,
      onKeyDown: sortable ? onKeyDownCallback : onKeyDown,
      scope,
    }),
    [sortable, onSortChangeCallback, onClick, onKeyDownCallback, onKeyDown, scope],
  );

  // Wrap children in sort button component when sortable
  const cellChildren = useMemo(
    () => (sortable ? <TableSortButton>{children}</TableSortButton> : children),
    [sortable, children],
  );

  // Build context value with conditional sorting state
  const context = useMemo(
    () => ({
      align,
      ...(sortable && { sorted: sortBy === value }),
    }),
    [align, sortable, sortBy, value],
  );

  return useMemo(
    () => ({
      align,
      context,
      props: {
        ...cellProps,
        children: cellChildren,
      },
      ref,
      sortable,
    }),
    [align, cellChildren, cellProps, context, sortable],
  );
};
